import { useState, useRef, useEffect } from "react";

interface DateRangePickerProps {
  from?: string;
  to?: string;
  onChange: (from: string | null, to: string | null) => void;
  style?: React.CSSProperties;
}

const DAYS = ["Lu", "Ma", "Me", "Gi", "Ve", "Sa", "Do"];
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function toDate(s: string | undefined): Date | undefined {
  if (!s) return undefined;
  const d = new Date(s + "T00:00:00");
  return isNaN(d.getTime()) ? undefined : d;
}
function toStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtShort(d: Date | undefined): string {
  if (!d) return "—";
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
}
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function inRange(d: Date, from: Date | undefined, to: Date | undefined): boolean {
  if (!from || !to) return false;
  const t = d.getTime();
  return t >= from.getTime() && t <= to.getTime();
}

function getCalendarDays(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0).getDate();
  const startDow = (first.getDay() + 6) % 7; // Monday=0
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const CELL = 26;

export function DateRangePicker({ from, to, onChange, style }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [selFrom, setSelFrom] = useState<Date | undefined>(toDate(from));
  const [selTo, setSelTo] = useState<Date | undefined>(toDate(to));
  const [hovering, setHovering] = useState<Date | undefined>();
  const [viewMonth, setViewMonth] = useState(() => {
    const d = toDate(from) || new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const ref = useRef<HTMLDivElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [alignRight, setAlignRight] = useState(false);

  useEffect(() => { setSelFrom(toDate(from)); setSelTo(toDate(to)); }, [from, to]);

  useEffect(() => {
    if (!open || !dropRef.current) return;
    const rect = dropRef.current.getBoundingClientRect();
    setAlignRight(rect.right > window.innerWidth - 8);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function handleDayClick(d: Date) {
    if (!selFrom || (selFrom && selTo)) {
      setSelFrom(d); setSelTo(undefined); setHovering(undefined);
    } else {
      const [a, b] = d < selFrom ? [d, selFrom] : [selFrom, d];
      setSelFrom(a); setSelTo(b); setHovering(undefined);
    }
  }

  function apply() {
    onChange(selFrom ? toStr(selFrom) : null, selTo ? toStr(selTo) : null);
    setOpen(false);
  }

  function quickSet(days: number) {
    const t = new Date(); const f = new Date();
    if (days > 0) f.setDate(t.getDate() - days);
    setSelFrom(f); setSelTo(t);
    onChange(toStr(f), toStr(t));
    setOpen(false);
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    setSelFrom(undefined); setSelTo(undefined); setHovering(undefined);
    onChange(null, null);
    setOpen(false);
  }

  function prevMonth() {
    setViewMonth((v) => v.month === 0 ? { year: v.year - 1, month: 11 } : { year: v.year, month: v.month - 1 });
  }
  function nextMonth() {
    setViewMonth((v) => v.month === 11 ? { year: v.year + 1, month: 0 } : { year: v.year, month: v.month + 1 });
  }

  const today = new Date();
  const hasRange = from || to;
  const label = selFrom
    ? selTo ? `${fmtShort(selFrom)} → ${fmtShort(selTo)}` : `${fmtShort(selFrom)} → ...`
    : "Periodo";

  const cells = getCalendarDays(viewMonth.year, viewMonth.month);
  const previewTo = selFrom && !selTo && hovering && hovering > selFrom ? hovering : undefined;

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block", ...style }}>
      <button type="button" className="btn btn-outline btn-sm" onClick={() => setOpen(!open)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "var(--space-2) var(--space-3)",
          fontSize: "var(--font-size-sm)",
          height: "100%", boxSizing: "border-box",
          fontWeight: hasRange ? 600 : 400,
          borderColor: hasRange ? "var(--indaco-brand-primary)" : undefined,
          color: hasRange ? "var(--indaco-brand-primary)" : undefined,
        }}>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>date_range</span>
        {label}
        {hasRange && (
          <span className="material-symbols-outlined" onClick={clear}
            style={{ fontSize: 12, opacity: 0.5, marginLeft: 2, cursor: "pointer" }}>close</span>
        )}
      </button>

      {open && (
        <div ref={dropRef} style={{
          position: "absolute", top: "100%", zIndex: 1000, marginTop: 2,
          ...(alignRight ? { right: 0 } : { left: 0 }),
          background: "var(--indaco-white)", border: "1px solid var(--indaco-gray-300)",
          borderRadius: "var(--border-radius-md)", boxShadow: "var(--shadow-md)",
          padding: 8, width: "max-content",
          fontFamily: "var(--font-family)", userSelect: "none",
        }}>
          {/* Month nav */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={prevMonth} style={{ padding: 2 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_left</span>
            </button>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--indaco-gray-800)" }}>
              {MONTHS[viewMonth.month]} {viewMonth.year}
            </span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={nextMonth} style={{ padding: 2 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
            </button>
          </div>

          {/* Day headers */}
          <div style={{ display: "grid", gridTemplateColumns: `repeat(7, ${CELL}px)`, gap: 0 }}>
            {DAYS.map((d) => (
              <div key={d} style={{ height: 20, display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 600, color: "var(--indaco-gray-500)", textTransform: "uppercase" }}>{d}</div>
            ))}
          </div>

          {/* Days grid */}
          <div style={{ display: "grid", gridTemplateColumns: `repeat(7, ${CELL}px)`, gap: 0 }}>
            {cells.map((d, i) => {
              if (!d) return <div key={`e${i}`} style={{ height: CELL }} />;
              const isToday = sameDay(d, today);
              const isStart = selFrom && sameDay(d, selFrom);
              const isEnd = (selTo && sameDay(d, selTo)) || (previewTo && sameDay(d, previewTo));
              const isMid = inRange(d, selFrom, selTo) || inRange(d, selFrom, previewTo);
              const isSelected = isStart || isEnd;

              let bg = "transparent";
              let fg = "var(--indaco-gray-800)";
              let weight = 400;
              let radius = "0";

              if (isSelected) {
                bg = "var(--indaco-brand-primary)"; fg = "var(--indaco-white)"; weight = 600; radius = "50%";
              } else if (isMid) {
                bg = "var(--indaco-brand-primary-light)"; fg = "var(--indaco-brand-primary)"; weight = 500;
              }

              return (
                <div key={i} onClick={() => handleDayClick(d)}
                  onMouseEnter={() => { if (selFrom && !selTo) setHovering(d); }}
                  style={{
                    height: CELL, display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: weight, cursor: "pointer",
                    background: bg, color: fg, borderRadius: radius,
                    border: isToday && !isSelected ? "1px solid var(--indaco-brand-primary)" : "1px solid transparent",
                    transition: "background 80ms",
                  }}>
                  {d.getDate()}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ borderTop: "1px solid var(--indaco-gray-200)", marginTop: 4, paddingTop: 4 }}>
            {/* Selection summary + apply */}
            {selFrom && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: "var(--indaco-brand-primary)", fontWeight: 600 }}>
                  {fmtShort(selFrom)}{selTo ? ` → ${fmtShort(selTo)}` : " → ..."}
                </span>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <button type="button" onClick={clear}
                    style={{
                      background: "none", border: "none", cursor: "pointer", padding: "2px 6px",
                      fontSize: 10, fontWeight: 600, borderRadius: 4, fontFamily: "var(--font-family)",
                      color: "var(--indaco-danger)",
                    }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 12 }}>close</span>
                  </button>
                  <button type="button" onClick={apply}
                    style={{
                      background: "var(--indaco-brand-primary)", border: "none", cursor: "pointer",
                      fontSize: 10, fontWeight: 600, fontFamily: "var(--font-family)",
                      color: "var(--indaco-white)", borderRadius: 3, padding: "2px 10px",
                    }}>Applica</button>
                </div>
              </div>
            )}
            {/* Quick shortcuts */}
            <div style={{ display: "flex", gap: 2 }}>
              {[["Oggi", 0], ["7gg", 7], ["30gg", 30]].map(([l, d]) => (
                <button key={l as string} type="button" className="btn-toggle-sm" onClick={() => quickSet(d as number)}>
                  {l as string}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

